import { useState, useEffect } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { Button, ButtonVariant, Body } from '@meiuca/ui-components'
import { venueBookingService } from '../../../services/venueBookingService'
import { modalitiesManager } from '../../../managers/modalitiesManager'

interface ModalityStepProps {
  selectedModality: string | null
  onSelectModality: (modality: string) => void
  venueId: string
}

export function ModalityStep({
  selectedModality,
  onSelectModality,
  venueId
}: ModalityStepProps) {
  const [modalities, setModalities] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchModalities() {
      try {
        setIsLoading(true)
        setError(null)

        // Carrega as modalidades no manager se ainda não foram carregadas
        await modalitiesManager.loadModalities()

        const data = await venueBookingService.getVenueModalities(venueId)
        setModalities(data)
      } catch (err) {
        console.error('Error fetching modalities:', err)
        setError('Erro ao carregar modalidades. Tente novamente.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchModalities()
  }, [venueId])

  return (
    <div className="flex flex-col h-full px-6 py-6">
      <div className="space-y-6">
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary mb-4" />
            <Body className="text-text-secondary">Carregando modalidades...</Body>
          </div>
        )}

        {error && !isLoading && (
          <div className="text-center py-8 space-y-4">
            <Body className="text-feedback-error mb-4">{error}</Body>
            <Button variant={ButtonVariant.PRIMARY} onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && modalities.length === 0 && (
          <div className="text-center py-8">
            <Body className="text-text-secondary">
              Nenhuma modalidade disponível para esta arena.
            </Body>
          </div>
        )}

        {!isLoading && !error && modalities.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modalities.map((modalityId) => {
              const modalityName = modalitiesManager.getModalityDescription(modalityId)
              const isSelected = selectedModality === modalityId

              return (
                <button
                  key={modalityId}
                  onClick={() => onSelectModality(modalityId)}
                  className={`
                    w-full p-4 rounded-xl border-2 transition-all hover:shadow-md
                    ${isSelected
                      ? 'border-brand-primary bg-brand-primary/5 shadow-md'
                      : 'border-border-light hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <h4 className="font-medium text-text-primary">
                        {modalityName}
                      </h4>
                    </div>
                    {isSelected && (
                      <Check className="w-5 h-5 text-brand-primary" />
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
